// InlineFeedbackApi.ts
import axios, { RawAxiosRequestHeaders } from "axios";
import Cookies from "universal-cookie";
import General from "../../../../general";
import config from "../../../../config";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
} from "../../tutorial/components/tutorial.types";

export interface UserAuth {
  userId: string;
  token: string;
}

export interface ApiHeaders {
  apiKey: string;
  userId?: string;
  token?: string;
  apisecret?: string;
}

interface GetUserAuthParams {
  apiKey: string;
  entityId: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  entityAuthenticationToken?: string;
}

interface FeedbackAction {
  actionId: string;
  metadata?: {
    ratingType?: string;
    leftRatingText?: string;
    rightRatingText?: string;
  };
}

interface Headers {
  heading?: string;
  subHeading?: string;
}

// Updated FeedbackResponse interface to include sdkConfig
interface FeedbackResponse {
  success: boolean;
  data: {
    actions: FeedbackAction[];
    campaignVariationId: string;
    sdkConfig?: {
      uiProps?: {
        questThemeData?: PartialQuestThemeData;
        BrandTheme?: {
          properties: PartialBrandTheme;
        };
        Headers?: Headers[] | undefined;
        showFooter?: boolean;
      };
    };
  };
}

interface ExternalLoginResponse {
  userId: string;
  token: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  userId: string;
  token: string;
}

class InlineFeedbackApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL =
      apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
    return {
      "Content-Type": "application/json",
      ...headers,
    };
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    entityAuthenticationToken,
  }: GetUserAuthParams): Promise<UserAuth | null> {
    try {
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId == uniqueUserId || externalUserId == uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken,
        };
      }

      if (uniqueUserId) {
        const headers = this.getAxiosHeaders({
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
          ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
        });

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post<ApiResponse<ExternalLoginResponse>>(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );

        if (!response.data?.success) {
          throw new Error("Invalid response from external login");
        }

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, {
          path: "/",
          expires,
        });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken,
        };
      }

      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async getFeedbackData(
    entityId: string,
    campaignId: string,
    headers: ApiHeaders,
    variation?: string
  ): Promise<FeedbackResponse | null> {
    try {
      if (!headers.userId || !headers.token) {
        throw new Error("Missing required headers");
      }

      const params = new URLSearchParams();
      if (variation) params.set("variation", variation);

      const axiosHeaders = this.getAxiosHeaders(headers);
      const request = `${
        this.BACKEND_URL
      }api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`;

      const response = await axios.get<FeedbackResponse>(request, {
        headers: axiosHeaders,
      });

      if (!response.data) {
        throw new Error("No data received from API");
      }

      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async submitFeedback(
    entityId: string,
    campaignId: string,
    headers: ApiHeaders,
    data: {
      actionId: string;
      answer: string;
      campaignVariationId: string;
    }
  ) {
    try {
      const axiosHeaders = this.getAxiosHeaders(headers);
      const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}/verify`;

      const body = {
        actions: [
          {
            actionId: data.actionId,
            answers: [data.answer],
          },
        ],
        campaignVariationId: data.campaignVariationId,
      };

      const response = await axios.post<ApiResponse<{ success: boolean }>>(
        request,
        body,
        { headers: axiosHeaders }
      );

      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }
}

export default InlineFeedbackApi;
